import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { transformedImageUrl } from '@/lib/imageTransform';

const sizeWidthPx: Record<string, number> = {
  xs: 40,
  sm: 64,
  md: 96,
  lg: 96,
  xl: 160,
};

interface UserAvatarProps {
  avatarUrl: string | null | undefined;
  username?: string | null;
  email?: string | null;
  className?: string;
  fallbackClassName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

const sizeClasses = {
  xs: 'h-5 w-5',
  sm: 'h-8 w-8',
  md: 'h-[42px] w-[42px]',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16 md:h-20 md:w-20',
};

const textSizeClasses = {
  xs: 'text-[8px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-xl',
};

/**
 * Sanitize avatar URL - returns undefined if the URL should not be used
 * (null, empty, or the legacy default-avatar.png placeholder)
 */
function sanitizeAvatarUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url === '/default-avatar.png' || url.endsWith('/default-avatar.png')) {
    return undefined;
  }
  return url;
}

/**
 * Generate initials from username or email
 * Returns 2 characters for normal sizes, 1 for xs
 */
function getInitials(username: string | null | undefined, email: string | null | undefined, size: string): string {
  const source = username || email;
  if (!source) return 'U';
  
  // For xs size, just return 1 character
  if (size === 'xs') {
    return source.charAt(0).toUpperCase();
  }
  
  // For normal sizes, return 2 characters
  if (source.length === 1) {
    return source.toUpperCase();
  }
  return source.substring(0, 2).toUpperCase();
}

/**
 * Centralized user avatar component with consistent styling across the app.
 * 
 * Features:
 * - Sanitizes avatar URLs (treats /default-avatar.png as no avatar)
 * - Consistent cyan glass fallback with initials everywhere
 * - Handles image load errors gracefully
 * - Consistent sizing via size prop
 */
export function UserAvatar({
  avatarUrl,
  username,
  email,
  className,
  fallbackClassName,
  size = 'md',
  onError,
}: UserAvatarProps) {
  const sanitizedUrl = sanitizeAvatarUrl(avatarUrl);
  const avatarPx = sizeWidthPx[size] ?? 96;
  const thumbUrl = sanitizedUrl
    ? (transformedImageUrl(sanitizedUrl, { width: avatarPx, height: avatarPx, quality: 75 }) ?? sanitizedUrl)
    : undefined;
  const initials = getInitials(username, email, size);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    // Fallback: try the original (un-transformed) URL if the thumbnail fails
    if (sanitizedUrl && target.src !== sanitizedUrl) {
      target.src = sanitizedUrl;
      return;
    }
    if (target.src.includes('?t=')) {
      target.src = target.src.split('?')[0];
    }
    onError?.(e);
  };

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      <AvatarImage
        src={thumbUrl}
        onError={handleError}
      />
      <AvatarFallback 
        className={cn(
          'bg-primary/35 text-white font-semibold border border-primary/30',
          textSizeClasses[size],
          fallbackClassName
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
